import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Download, X, Check, Loader2 } from 'lucide-react';
import { projectsAPI } from '../../../services/api';
import { getExportFilename, downloadVideo } from '../../../utils/puppeteerExportUtils';

/**
 * Notification Bell Component (unchanged)
 */
export const ExportNotificationBell = ({ notifications, onDownload, onRemove, projectName }) => {
  const [isOpen, setIsOpen] = useState(false);

  const completedCount = notifications.filter(n => n.status === 'complete').length;
  const processingCount = notifications.filter(n => n.status === 'processing').length;
  const errorCount = notifications.filter(n => n.status === 'error').length;
  const hasNotifications = completedCount > 0 || processingCount > 0 || errorCount > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.notification-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const handleDownload = (downloadUrl, jobId) => {
    const notification = notifications.find(n => n.jobId === jobId);
    const format = notification?.format || 'mp4';
    const name = notification?.projectName || projectName || 'Untitled';

    const filename = getExportFilename(name, format);
    downloadVideo(downloadUrl, filename);
  };

  return (
    <div className="relative notification-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg bg-ag-green hover:bg-green-500 transition-colors"
        title="Export notifications"
      >
        <Bell size={20} className="text-black" />

        {/* Notification badge */}
        {hasNotifications && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
            {completedCount + processingCount + errorCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-ag-black-700 border border-ag-black-400 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-ag-black-400 flex items-center justify-between">
            <h3 className="font-bold text-white">Export Notifications</h3>
            {hasNotifications && (
              <span className="text-xs text-gray-400">
                {completedCount > 0 && `${completedCount} complete`}
                {completedCount > 0 && (processingCount > 0 || errorCount > 0) && ', '}
                {processingCount > 0 && `${processingCount} in progress`}
                {processingCount > 0 && errorCount > 0 && ', '}
                {errorCount > 0 && `${errorCount} failed`}
              </span>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Bell size={48} className="mx-auto mb-2 opacity-20" />
                <p>No export notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-ag-black-400">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.jobId}
                    notification={notification}
                    onDownload={handleDownload}
                    onRemove={onRemove}
                    formatTimeAgo={formatTimeAgo}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Individual notification item (unchanged)
 */
const NotificationItem = ({ notification, onDownload, onRemove, formatTimeAgo }) => {
  const { jobId, projectName, status, progress, stage, downloadUrl, error, timestamp } = notification;

  const getStatusIcon = () => {
    switch (status) {
      case 'complete':
        return <Check size={20} className="text-ag-green" />;
      case 'error':
        return <X size={20} className="text-red-400" />;
      case 'processing':
        return <Loader2 size={20} className="text-ag-green animate-spin" />;
      default:
        return <Loader2 size={20} className="text-gray-400 animate-spin" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'complete':
        return 'Export complete';
      case 'error':
        return 'Export failed';
      case 'processing':
        return `${stage || 'Processing'}... ${Math.round(progress)}%`;
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="p-4 hover:bg-ag-black-800 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-1">
            {getStatusIcon()}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">
              {projectName}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {getStatusText()}
            </p>

            {status === 'processing' && (
              <div className="w-full bg-ag-black-900 rounded-full h-1.5 mt-2">
                <div
                  className="bg-ag-green h-full transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}

            {error && (
              <p className="text-xs text-red-400 mt-1 truncate" title={error}>
                {error}
              </p>
            )}

            <p className="text-xs text-gray-500 mt-1">
              {formatTimeAgo(timestamp)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {status === 'complete' && downloadUrl && (
            <button
              onClick={() => onDownload(downloadUrl, jobId)}
              className="p-2 bg-ag-green hover:bg-green-500 rounded-lg transition-colors"
              title="Download"
            >
              <Download size={16} className="text-black" />
            </button>
          )}

          <button
            onClick={() => onRemove(jobId)}
            className="p-2 hover:bg-ag-black-600 rounded-lg transition-colors"
            title="Dismiss"
          >
            <X size={16} className="text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};